// NexTrainE — Brand Constants

export const BRAND = {
  name: 'NexTrainE',
  payoff: 'Engineering What\'s Next',
  tagline: 'Mountain-born. Industry-ready.',
  legal: 'NexTrainE S.r.l.',
  vat: 'P.IVA 02223360666',
  university: 'Università degli Studi dell\'Aquila',
  address: 'Via Vetoio, SNC c/o Università degli Studi dell\'Aquila, Coppito 1, DISIM',
  postalCode: '67100',
  city: 'L\'Aquila, Italia',
  province: 'AQ',
  email: 'info@nextraine.com', // PLACEHOLDER
  /** SDI recipient code (fatturazione elettronica), via Fatture in Cloud/TS Digital. */
  sdi: 'M5UXCR1',
} as const;

export interface Founder {
  name: string;
  role: string;
  linkedin: string;
  photo?: string;
}

export const FOUNDERS: Founder[] = [
  { name: 'Carlo Centofanti', role: 'Co-Founder', linkedin: 'https://www.linkedin.com/in/carlo-centofanti1/', photo: '/team/CC.png' },
  { name: 'Andrea Marotta', role: 'Co-Founder', linkedin: 'https://www.linkedin.com/in/andrea-marotta-730583148/', photo: '/team/AM.jpeg' },
  { name: 'Luigi Susanna', role: 'Co-Founder', linkedin: 'https://www.linkedin.com/in/luigi-susanna-9b448520/', photo: '/team/LS.jpeg' },
];

export const TECH_ITEMS = [
  { name: 'AWS', category: 'Cloud' },
  { name: 'Lambda', category: 'Compute' },
  { name: 'DynamoDB', category: 'Database' },
  { name: 'CloudFront', category: 'CDN' },
  { name: 'S3', category: 'Storage' },
  { name: 'React', category: 'Frontend' },
  { name: 'TypeScript', category: 'Language' },
  { name: 'Python', category: 'Language' },
  { name: 'TensorFlow', category: 'ML' },
  { name: 'Docker', category: 'DevOps' },
  { name: 'Kubernetes', category: 'Orchestration' },
  { name: 'MQTT', category: 'IoT' },
  { name: 'CDK', category: 'IaC' },
  { name: 'Grafana', category: 'Observability' },
] as const;

export interface PortfolioItem {
  id: string;
  status: {
    it: string;
    en: string;
  };
  client: {
    it: string;
    en: string;
  };
  title: {
    it: string;
    en: string;
  };
  category: {
    it: string;
    en: string;
  };
  desc: {
    it: string;
    en: string;
  };
  tags: string[];
  thumbnail?: string;
  challenge?: {
    it: string;
    en: string;
  };
  solution?: {
    it: string;
    en: string;
  };
  impact?: {
    it: string;
    en: string;
  };
  stats?: Array<{
    value: string;
    label: {
      it: string;
      en: string;
    };
  }>;
}

export const PORTFOLIO_ITEMS: PortfolioItem[] = [
  {
    id: 'medwatch',
    status: {
      it: 'In Corso (WIP)',
      en: 'Work in Progress (WIP)',
    },
    client: {
      it: 'Riservato (Spin-off MedTech & Startup)',
      en: 'Confidential (MedTech Spin-off & Startup)',
    },
    category: {
      it: 'Healthcare & IoT',
      en: 'Healthcare & IoT',
    },
    title: {
      it: 'MedWatch: Monitoraggio ECG e Respiro per Dispositivi Smart',
      en: 'MedWatch: ECG & Respiration Monitoring for Smart Devices',
    },
    desc: {
      it: 'Piattaforma clinica comprendente web dashboard e app native iOS e Android per la visualizzazione remota di tracciati fisiologici stile Holter (ECG e respiro) campionati a 500Hz.',
      en: 'Clinical platform including web dashboard and native iOS and Android apps for remote viewing of Holter-style physiological traces (ECG & respiration) sampled at 500Hz.',
    },
    tags: ['MedTech', 'GDPR', 'iOS & Android', 'HTML5 Canvas', 'AWS Cloud'],
    thumbnail: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&w=800&q=80',
    challenge: {
      it: 'Consentire ai medici di visualizzare e analizzare in modo fluido enormi tracciati multi-canale campionati a 500Hz, stile Holter, e consentire ai pazienti di monitorare i propri parametri tramite app mobile dedicate, senza costringere il paziente a tornare fisicamente in studio per scaricare i dati dal dispositivo. Questo abilita il monitoraggio continuativo per identificare patologie sporadiche che non si manifestano nelle classiche 24/48 ore.',
      en: 'Allowing physicians to smoothly view and analyze massive multi-channel physiological traces sampled at 500Hz, Holter-style, and letting patients monitor parameters via dedicated mobile apps, without forcing the patient to return to the clinic to offload device data. This enables continuous remote monitoring to catch sporadic pathologies that do not always manifest in standard 24/48-hour periods.',
    },
    solution: {
      it: 'Sviluppo di una piattaforma cloud-native con web dashboard e app native iOS/Android. Il visualizzatore grafico su smartphone sfrutta rendering accelerato fino a 144Hz (a seconda delle capacità del device) per riprodurre fedelmente la forma d\'onda del tracciato. Invece del monitoraggio in tempo reale, la piattaforma gestisce l\'ingestion differita di intere sessioni di tracciamento caricate dal paziente da remoto, fornendo ai cardiologi strumenti avanzati di zoom, misurazione e annotazione delle anomalie sulla dashboard web.',
      en: 'Development of a cloud-native platform with a web dashboard and native iOS/Android apps. The mobile graphic viewer leverages hardware-accelerated rendering up to 144Hz (depending on device capabilities) to faithfully reproduce trace waveforms. Rather than real-time tracking, the platform handles batch ingestion of full tracking sessions uploaded by patients remotely, providing cardiologists with advanced zoom, measurement, and anomaly annotation tools on the web dashboard.',
    },
    impact: {
      it: 'I medici possono esaminare ed esportare report diagnostici completi da qualsiasi dispositivo. La possibilità di analizzare sessioni prolungate ha aumentato significativamente la diagnosi di aritmie silenti, riducendo le visite in presenza.',
      en: 'Physicians can examine and export complete diagnostic reports from any device. The ability to analyze extended sessions significantly increased the detection of silent arrhythmias, reducing in-office clinical visits.',
    },
    stats: [
      {
        value: '500Hz',
        label: {
          it: 'Frequenza campioni',
          en: 'Sampling Frequency',
        },
      },
      {
        value: '144Hz',
        label: {
          it: 'Rendering Mobile (Fino a)',
          en: 'Mobile rendering (Up to)',
        },
      },
      {
        value: 'GDPR',
        label: {
          it: 'Conformità privacy',
          en: 'Privacy Compliance',
        },
      },
    ],
  },
  {
    id: 'pastacloud',
    status: {
      it: 'In Corso (WIP)',
      en: 'Work in Progress (WIP)',
    },
    client: {
      it: 'Riservato (Pastificio Industriale)',
      en: 'Confidential (Industrial Pasta Manufacturer)',
    },
    category: {
      it: 'Industrial IoT & MES',
      en: 'Industrial IoT & MES',
    },
    title: {
      it: 'PastaCloud: Digitalizzazione di Pastificio Industriale',
      en: 'PastaCloud: Industrial Pasta Factory Digitalization',
    },
    desc: {
      it: 'Piattaforma Smart MES ed IoT per monitorare in tempo reale i cicli di impasto, i parametri di essiccazione (temperatura/umidità) e garantire la tracciabilità completa dei lotti.',
      en: 'Smart MES & IoT platform for real-time monitoring of kneading cycles, drying parameters (temperature/humidity), and guaranteeing full batch traceability.',
    },
    tags: ['Industrial IoT', 'OPC UA', 'Time-series DB', 'Grafana'],
    thumbnail: 'https://images.unsplash.com/photo-1551462147-ff29053bfc14?auto=format&fit=crop&w=800&q=80',
    challenge: {
      it: 'La produzione di pasta industriale (rigatoni, spaghetti, ecc.) richiede fasi di essiccazione altamente controllate. La mancanza di sensori connessi e la tracciabilità cartacea portavano ad anomalie qualitative e a un tasso medio di scarto del 12%.',
      en: 'Industrial pasta production (rigatoni, spaghetti, etc.) requires tightly controlled drying phases. The lack of connected sensors and paper-based traceability led to quality variations and a 12% average scrap rate.',
    },
    solution: {
      it: 'Sviluppo di PastaCloud, una suite MES con tracciamento lotti integrato. Abbiamo collegato le celle di essiccazione e le impastatrici tramite protocollo OPC UA ad una pipeline cloud locale, monitorando umidità, temperature ed efficienza dei macchinari.',
      en: 'Development of PastaCloud, a MES suite with integrated batch tracking. We connected drying chambers and kneading machines via OPC UA protocol to a local cloud pipeline, monitoring humidity, temperatures, and machine efficiency.',
    },
    impact: {
      it: 'I sensori e le dashboard real-time hanno permesso di abbattere gli scarti da essiccazione del 15% e digitalizzare al 100% la tracciabilità delle materie prime (semola di grano duro) fino al pacco di pasta finito.',
      en: 'Real-time sensors and dashboards reduced drying scrap rates by 15% and digitized raw material (durum wheat semolina) tracking down to the final package at 100%.',
    },
    stats: [
      {
        value: '100%',
        label: {
          it: 'Tracciabilità digitale',
          en: 'Digital traceability',
        },
      },
      {
        value: '-15%',
        label: {
          it: 'Scarti essiccazione',
          en: 'Drying scrap rate',
        },
      },
      {
        value: 'OPC UA',
        label: {
          it: 'Integrazione PLC',
          en: 'PLC Integration',
        },
      },
    ],
  },
  {
    id: 'nexconf',
    status: {
      it: 'In Corso (WIP)',
      en: 'Work in Progress (WIP)',
    },
    client: {
      it: 'Riservato (Società Scientifica Internazionale)',
      en: 'Confidential (International Scientific Society)',
    },
    category: {
      it: 'SaaS & Web App',
      en: 'SaaS & Web App',
    },
    title: {
      it: 'NexConf: Gestione e Peer Review per Conferenze Accademiche',
      en: 'NexConf: Management & Peer Review for Academic Conferences',
    },
    desc: {
      it: 'Una piattaforma moderna, veloce e reattiva per rimpiazzare i complessi software legacy (tipo EDAS). Gestisce la sottomissione dei paper, il flusso di peer review e l\'assegnazione automatica ai reviewer.',
      en: 'A modern, fast, and responsive platform to replace complex legacy conference software (such as EDAS). It manages paper submission, peer review workflows, and automated reviewer assignment.',
    },
    tags: ['React', 'Serverless', 'TypeScript', 'Node.js'],
    thumbnail: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?auto=format&fit=crop&w=800&q=80',
    challenge: {
      it: 'Gli strumenti correnti di organizzazione conferenze scientifiche hanno una latenza di caricamento frustrante (>5s) ed interfacce obsolete che complicano le attività di peer review distribuite tra centinaia di docenti e ricercatori.',
      en: 'Current scientific conference tools suffer from frustrating loading latency (>5s) and legacy interfaces that complicate peer review workflows distributed among hundreds of professors and researchers.',
    },
    solution: {
      it: 'Progettazione di NexConf su architettura cloud nativa ed API a bassissima latenza. Abbiamo realizzato una dashboard ad alta usabilità e implementato un algoritmo euristico per l\'abbinamento paper-reviewer per parole chiave.',
      en: 'Design of NexConf on a cloud-native architecture with ultra-low latency APIs. We built a high-usability dashboard and implemented a keyword-based heuristic algorithm for automated paper-reviewer matching.',
    },
    impact: {
      it: 'Latenza di interazione ridotta a meno di 150ms, riduzione del 60% del tempo speso nell\'organizzazione delle sessioni ed abbinamento automatico per sottomissioni parallele massive.',
      en: 'Interaction latency reduced to under 150ms, a 60% reduction in session organization time, and automated assignment capability scaled for massive parallel submissions.',
    },
    stats: [
      {
        value: '< 150ms',
        label: {
          it: 'Tempo risposta UI',
          en: 'UI Response Time',
        },
      },
      {
        value: '-60%',
        label: {
          it: 'Tempo organizzazione',
          en: 'Organization Time',
        },
      },
      {
        value: '100%',
        label: {
          it: 'Assegnazione paper',
          en: 'Automated assignment',
        },
      },
    ],
  },
  {
    id: 'clips',
    status: {
      it: 'In Corso (WIP)',
      en: 'Work in Progress (WIP)',
    },
    client: {
      it: 'Riservato (Impresa di Costruzioni Generali & Infrastrutture)',
      en: 'Confidential (General Contractor & Infrastructure)',
    },
    category: {
      it: 'Smart IoT & Safety',
      en: 'Smart IoT & Safety',
    },
    title: {
      it: 'CLIPS: Smart Safety Harness e Piattaforma di Monitoraggio',
      en: 'CLIPS: Smart Safety Harness & Monitoring Platform',
    },
    desc: {
      it: 'Piattaforma di sicurezza sul lavoro basata su imbracature intelligenti con sensori per il rilevamento del corretto aggancio delle cinture e notifica immediata ai supervisori.',
      en: 'Workplace safety platform powered by smart safety harnesses with integrated sensors to detect correct harness anchoring and alert supervisors instantly.',
    },
    tags: ['Smart IoT', 'Safety Automation', 'Edge Sensors', 'Cloud'],
    thumbnail: 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&w=800&q=80',
    challenge: {
      it: 'Le operazioni in quota nei cantieri presentano gravi rischi legati a cadute dall\'alto. Le imbracature tradizionali non offrono visibilità sul corretto aggancio dei moschettoni e dei sistemi di protezione, ostacolando interventi preventivi e tempestivi.',
      en: 'High-altitude operations in construction and industrial sites present severe risks related to falls. Traditional safety harnesses offer no visibility into whether protection carabiners are correctly anchored, preventing timely interventions.',
    },
    solution: {
      it: 'Sviluppo di CLIPS, una piattaforma cloud centrale integrata con smart safety harness dotati di sensori intelligenti. In caso di mancato o scorretto aggancio ai sistemi di protezione in quota, il dispositivo emette un segnale acustico immediato per l\'operatore e notifica automaticamente via cloud un supervisore.',
      en: 'Development of CLIPS, a central cloud platform linked with smart safety harnesses equipped with intelligent sensors. In case of incorrect or missing anchoring to fall protection systems, the device emits an immediate acoustic warning for the operator and automatically triggers a digital notification to a supervisor via cloud.',
    },
    impact: {
      it: 'Aumento significativo della sicurezza per gli operai in quota, rilevamento preventivo delle situazioni di rischio e promozione di una cultura della sicurezza basata su comportamenti responsabili.',
      en: 'Significant safety improvements for high-altitude workers, proactive risk situation detection, and fostering of a safety culture based on responsible behavior.',
    },
    stats: [
      {
        value: '< 1s',
        label: {
          it: 'Tempo di notifica',
          en: 'Alert Latency',
        },
      },
      {
        value: '100%',
        label: {
          it: 'Visibilità aggancio',
          en: 'Anchoring Visibility',
        },
      },
      {
        value: 'IoT',
        label: {
          it: 'Sensori intelligenti',
          en: 'Smart Sensors',
        },
      },
    ],
  },
];

export interface SegmentItem {
  id: string;
  title: {
    it: string;
    en: string;
  };
  desc: {
    it: string;
    en: string;
  };
  pain: {
    it: string;
    en: string;
  };
  longDesc: {
    it: string;
    en: string;
  };
  benefits: Array<{
    title: {
      it: string;
      en: string;
    };
    desc: {
      it: string;
      en: string;
    };
  }>;
  services: Array<{
    it: string;
    en: string;
  }>;
  caseStudies: string[];
}

export const SEGMENT_ITEMS: SegmentItem[] = [
  {
    id: 'medtech',
    title: {
      it: 'MedTech & Healthcare',
      en: 'MedTech & Healthcare',
    },
    desc: {
      it: 'Soluzioni sicure e conformi per la gestione e l\'integrazione dei dati da dispositivi medici e sensori connessi. Compliance GDPR e normative di settore.',
      en: 'Secure, compliant solutions for managing and integrating data from medical devices and connected sensors. GDPR and sector-specific regulatory compliance.',
    },
    pain: {
      it: 'Sicurezza, compliance, interoperabilità dei dati clinici',
      en: 'Data security, compliance, clinical data interoperability',
    },
    longDesc: {
      it: 'Nel settore MedTech, la sicurezza dei dati e la conformità normativa non sono opzionali. NexTrainE aiuta i produttori di dispositivi medici e i fornitori di servizi sanitari a progettare e implementare architetture per l\'ingestion sicura e l\'analisi in tempo reale dei dati fisiologici dei pazienti. Il nostro approccio si basa sui principi del secure-by-design e zero-trust, assicurando il rispetto del GDPR e dei requisiti di riservatezza più stringenti.',
      en: 'In the MedTech sector, data security and regulatory compliance are non-negotiable. NexTrainE helps medical device manufacturers and healthcare providers design and implement architectures for the secure ingestion and real-time analysis of physiological patient data. Our approach is based on secure-by-design and zero-trust principles, ensuring compliance with GDPR and the most stringent confidentiality requirements.',
    },
    benefits: [
      {
        title: {
          it: 'Compliance GDPR Garantita',
          en: 'Guaranteed GDPR Compliance',
        },
        desc: {
          it: 'Anonimizzazione all\'edge, cifratura end-to-end dei dati sanitari e controllo degli accessi granulare basato su ruoli (RBAC).',
          en: 'Edge-based anonymization, end-to-end encryption of health data, and granular role-based access control (RBAC).',
        },
      },
      {
        title: {
          it: 'Interoperabilità Clinica',
          en: 'Clinical Interoperability',
        },
        desc: {
          it: 'Integrazione dei flussi di dati provenienti da dispositivi biomedicali con standard sanitari riconosciuti (HL7, FHIR, DICOM).',
          en: 'Integration of data streams from biomedical devices with recognized healthcare standards (HL7, FHIR, DICOM).',
        },
      },
      {
        title: {
          it: 'Architetture Zero-Trust',
          en: 'Zero-Trust Architectures',
        },
        desc: {
          it: 'Isolamento totale delle identità dei pazienti e crittografia avanzata sia a riposo che in transito.',
          en: 'Absolute patient identity isolation and advanced encryption both at rest and in transit.',
        },
      },
    ],
    services: [
      {
        it: 'Progettazione di Gateway IoT Medicali sicuri ed edge computing',
        en: 'Design of secure Medical IoT Gateways and edge computing modules',
      },
      {
        it: 'Anonimizzazione e pseudonimizzazione dei dati sanitari in tempo reale',
        en: 'Real-time health data anonymization and pseudonymization',
      },
      {
        it: 'Configurazione di sistemi di cifratura avanzata e gestione chiavi via KMS',
        en: 'Configuration of advanced encryption systems and key management via KMS',
      },
      {
        it: 'Sviluppo firmware sicuro conforme a standard biomedicali',
        en: 'Secure firmware development compliant with biomedical standards',
      },
      {
        it: 'Integrazione pipeline cloud serverless conformi GDPR su AWS',
        en: 'Integration of serverless cloud pipelines compliant with GDPR on AWS',
      },
    ],
    caseStudies: ['medwatch'],
  },
  {
    id: 'manufacturing',
    title: {
      it: 'Manifatturiero & Industria',
      en: 'Manufacturing & Industry',
    },
    desc: {
      it: 'Integrazione di soluzioni AI nei processi produttivi delle PMI. Miglioramento di qualità, efficienza e tracciabilità con tecnologie già disponibili sul mercato.',
      en: 'Integration of AI solutions into SME production processes. Improving quality, efficiency, and traceability with market-available technologies.',
    },
    pain: {
      it: 'Qualità, efficienza, tracciabilità nei processi produttivi',
      en: 'Quality, efficiency, and traceability in production',
    },
    longDesc: {
      it: 'L\'industria moderna richiede efficienza operativa e tracciabilità totale dei lotti. Aiutiamo le PMI manifatturiere a digitalizzare i propri impianti integrando pipeline di dati edge-to-cloud e modelli di intelligenza artificiale per l\'ottimizzazione del processo produttivo. Dalla telemetria real-time delle macchine alla manutenzione predittiva, abilitiamo le aziende a ridurre lo scarto e prevenire fermi macchina improvvisi senza stravolgere i sistemi esistenti.',
      en: 'Modern industry demands operational efficiency and total batch traceability. We help manufacturing SMEs digitalize their plants by integrating edge-to-cloud data pipelines and artificial intelligence models for process optimization. From real-time machine telemetry to predictive maintenance, we enable companies to reduce scrap and prevent unplanned downtime without disrupting existing systems.',
    },
    benefits: [
      {
        title: {
          it: 'Riduzione dello Scarto',
          en: 'Scrap Reduction',
        },
        desc: {
          it: 'Monitoraggio continuo dei parametri critici di lavorazione e allarmi tempestivi sulle derive fisiche di processo.',
          en: 'Continuous monitoring of critical machining parameters and early warnings on physical process drifts.',
        },
      },
      {
        title: {
          it: 'Fermo Macchina Evitato',
          en: 'Prevented Downtime',
        },
        desc: {
          it: 'Modelli AI di manutenzione predittiva basati su analisi vibrazionale (FFT), acustica ed anomalie storiche.',
          en: 'Predictive maintenance AI models based on vibrational (FFT), acoustic, and historical anomaly analysis.',
        },
      },
      {
        title: {
          it: 'Integrazione MES / Industria 4.0',
          en: 'MES / Industry 4.0 Integration',
        },
        desc: {
          it: 'Raccolta dati certificata da sensori IoT a livello PLC/Gateway per l\'automazione industriale e la rendicontazione.',
          en: 'Certified data collection from IoT sensors at PLC/Gateway level for industrial automation and reporting.',
        },
      },
    ],
    services: [
      {
        it: 'Sviluppo di pipeline edge-to-cloud per telemetria macchine in tempo reale',
        en: 'Development of edge-to-cloud pipelines for real-time machine telemetry',
      },
      {
        it: 'Integrazione sensori industriali e protocolli di campo (OPC-UA, Modbus, MQTT)',
        en: 'Integration of industrial sensors and field protocols (OPC-UA, Modbus, MQTT)',
      },
      {
        it: 'Sviluppo ed adattamento modelli AI di manutenzione predittiva (vibrazioni, acustica, termografia)',
        en: 'Development and adaptation of predictive maintenance AI models (vibrational, acoustic, thermographic)',
      },
      {
        it: 'Analisi predittiva scarti e sistemi di controllo qualità automatizzati',
        en: 'Predictive scrap analysis and automated quality control systems',
      },
      {
        it: 'Dashboarding personalizzato per telemetria OEE e monitoraggio impianti industriali (Grafana)',
        en: 'Customized dashboarding for OEE telemetry and industrial plant monitoring (Grafana)',
      },
    ],
    caseStudies: ['pastacloud', 'clips'],
  },
  {
    id: 'it',
    title: {
      it: 'Aziende IT & System Integrator',
      en: 'IT Companies & System Integrators',
    },
    desc: {
      it: 'Partnership per sviluppare progetti congiunti in ambito IoT, data pipeline e observability. Rafforziamo la vostra offerta verso i clienti finali.',
      en: 'Partnerships to develop joint projects in IoT, data pipelines, and observability. We strengthen your offering to end customers.',
    },
    pain: {
      it: 'Estendere l\'offerta con competenze IoT e data engineering',
      en: 'Extending capabilities with IoT and data engineering expertise',
    },
    longDesc: {
      it: 'Spesso le aziende di consulenza IT e i system integrator hanno la necessità di integrare competenze verticali nello sviluppo di pipeline dati complesse, sistemi embedded custom e osservabilità cloud avanzata. NexTrainE si propone come partner tecnologico qualificato, collaborando in co-development per realizzare soluzioni edge-to-cloud sicure, performanti e scalabili.',
      en: 'Often IT consulting companies and system integrators need to integrate specialized skills in developing complex data pipelines, custom embedded systems, and advanced cloud observability. NexTrainE acts as a qualified technological partner, collaborating in co-development to create secure, high-performance, and scalable edge-to-cloud solutions.',
    },
    benefits: [
      {
        title: {
          it: 'Co-Development Agile',
          en: 'Agile Co-Development',
        },
        desc: {
          it: 'Integrazione fluida nei vostri team di sviluppo, condivisione delle repository e delivery puntuale del software.',
          en: 'Seamless integration into your development teams, repository sharing, and on-time software delivery.',
        },
      },
      {
        title: {
          it: 'Trasferimento Tecnologico',
          en: 'Technology Transfer',
        },
        desc: {
          it: 'Esperienza accademica e di ricerca in cloud, distributed systems ed IoT trasferita direttamente nei vostri progetti commerciali.',
          en: 'Academic and research experience in cloud, distributed systems, and IoT transferred directly to your commercial projects.',
        },
      },
      {
        title: {
          it: 'Observability & Cloud Native',
          en: 'Observability & Cloud Native',
        },
        desc: {
          it: 'Uso di infrastrutture serverless, Infrastructure-as-Code (AWS CDK) e tracciamento distribuito con standard aperti (OpenTelemetry).',
          en: 'Use of serverless infrastructure, Infrastructure-as-Code (AWS CDK), and distributed tracing with open standards (OpenTelemetry).',
        },
      },
    ],
    services: [
      {
        it: 'Consulenza su architetture cloud serverless e Infrastructure-as-Code (IaC)',
        en: 'Consulting on serverless cloud architectures and Infrastructure-as-Code (IaC)',
      },
      {
        it: 'Sviluppo di moduli software edge ed integrazioni IoT custom',
        en: 'Development of edge software modules and custom IoT integrations',
      },
      {
        it: 'Integrazione e configurazione di OpenTelemetry per osservabilità distribuita nei microservizi',
        en: 'Integration and configuration of OpenTelemetry for distributed observability in microservices',
      },
      {
        it: 'Ottimizzazione delle performance delle infrastrutture e riduzione dei costi cloud',
        en: 'Infrastructure performance optimization and cloud cost reduction',
      },
      {
        it: 'Supporto nella progettazione e review di sistemi distribuiti critici ad alta disponibilità',
        en: 'Support in designing and reviewing critical high-availability distributed systems',
      },
    ],
    caseStudies: ['nexconf'],
  },
];

export interface ResearchPageContent {
  pain: { it: string; en: string };
  title: { it: string; en: string };
  subtitle: { it: string; en: string };
  longDesc: { it: string; en: string };
  benefits: Array<{
    title: { it: string; en: string };
    desc: { it: string; en: string };
  }>;
  services: Array<{ it: string; en: string }>;
  caseStudies: string[];
}

export const RESEARCH_PAGE: ResearchPageContent = {
  pain: {
    it: 'Raggiungere KPI e deliverable di progetto',
    en: 'Meeting project KPIs & deliverables',
  },
  title: {
    it: 'Ricerca Scientifica & Accademica',
    en: 'Scientific & Academic Research',
  },
  subtitle: {
    it: 'Trasformiamo obiettivi di ricerca in software funzionante — dalla progettazione alla scrittura dei deliverable tecnici.',
    en: 'We turn research objectives into working software — from design to writing technical deliverables.',
  },
  longDesc: {
    it: 'I gruppi di ricerca affrontano spesso lo stesso ostacolo: obiettivi scientifici ambiziosi, KPI di progetto da rispettare, e poco tempo o competenze interne per costruire il software necessario a raggiungerli. NexTrainE nasce come spin-off universitario proprio per colmare questo divario: conosciamo il linguaggio della ricerca, i vincoli dei bandi e la pressione delle scadenze di progetto, e mettiamo questa esperienza al servizio di gruppi di ricerca, dipartimenti e consorzi che devono trasformare un\'idea scientifica in una piattaforma software solida e dimostrabile.',
    en: "Research groups often face the same obstacle: ambitious scientific goals, project KPIs to meet, and little time or in-house expertise to build the software needed to reach them. NexTrainE was founded as a university spin-off precisely to bridge this gap: we speak the language of research, understand grant constraints and project deadlines, and put that experience to work for research groups, departments, and consortia that need to turn a scientific idea into a solid, demonstrable software platform.",
  },
  benefits: [
    {
      title: { it: 'Progettazione su Misura', en: 'Tailored Design' },
      desc: {
        it: 'Analizziamo obiettivi scientifici e KPI di progetto per progettare un\'architettura software realistica, allineata ai tempi e al budget del bando.',
        en: "We analyze scientific goals and project KPIs to design a realistic software architecture, aligned with the grant's timeline and budget.",
      },
    },
    {
      title: { it: 'Implementazione End-to-End', en: 'End-to-End Implementation' },
      desc: {
        it: 'Sviluppiamo il software — dashboard, pipeline dati, prototipi, piattaforme — necessario a dimostrare i risultati previsti dal progetto.',
        en: 'We build the software — dashboards, data pipelines, prototypes, platforms — needed to demonstrate the results your project promised.',
      },
    },
    {
      title: { it: 'Supporto ai Deliverable', en: 'Deliverable Support' },
      desc: {
        it: 'Affianchiamo il team nella stesura della documentazione tecnica e dei deliverable di progetto, con un linguaggio chiaro per revisori e stakeholder.',
        en: 'We support the team in writing technical documentation and project deliverables, in language reviewers and stakeholders can follow.',
      },
    },
  ],
  services: [
    {
      it: 'Progettazione dell\'architettura software per raggiungere KPI e milestone di progetto',
      en: 'Software architecture design to meet project KPIs and milestones',
    },
    {
      it: 'Sviluppo di prototipi e MVP per dimostrare i risultati della ricerca',
      en: 'Prototype and MVP development to demonstrate research outcomes',
    },
    {
      it: 'Assistenza tecnica nella scrittura di deliverable e reportistica di progetto',
      en: 'Technical assistance writing project deliverables and reports',
    },
    {
      it: 'Gestione e strutturazione dei dati secondo i principi FAIR e gli standard richiesti dal bando',
      en: 'Data management and structuring following FAIR principles and grant-required standards',
    },
    {
      it: 'Supporto continuativo lungo l\'intero ciclo di vita del progetto di ricerca',
      en: 'Ongoing support across the entire research project lifecycle',
    },
  ],
  caseStudies: ['nexconf'],
};
